<script>
import { getApiRefPage } from '@/utils/api/contentful';
import { mapActions } from 'vuex';
import setMetaData from '@/utils/setMetaData';
import ApiReferencePage from '@/components/apiReferencePage/Page.vue';
import PageNotFound from '@/components/shared/PageNotFound.vue';

export default {
  name: 'PageApiReference',

  components: {
    ApiReferencePage,
    PageNotFound,
  },

  data() {
    return {
      page: null,
      versions: [],
      content: null,
      showContent: true,
    };
  },

  watch: {
    $route(to, from) {
      if (!to.params.slug) {
        return;
      }

      if (to.params.slug !== from.params.slug) {
        this.fetchPage();
        return;
      }

      if (!to.params.version) {
        this.redirectToLatestPageVersion(to.params.slug);
        return;
      }

      if (to.params.version !== from.params.version) {
        this.showContent = false;

        this.setContent();

        window.setTimeout(() => {
          this.showContent = true;
        }, 200);
      }
    },
  },

  created() {
    this.fetchPage();
  },

  methods: {
    ...mapActions('settings', ['setIsPageLoading']),

    async fetchPage() {
      const { slug } = this.$route.params;

      try {
        this.setIsPageLoading(true);
        this.page = await getApiRefPage(slug);
        this.setIsPageLoading(false);

        if (!this.page) {
          return;
        }

        setMetaData({
          title: `API Reference - ${this.page.fields.title}`,
          description: this.page.fields.description,
        });

        if (!this.page.fields.versions) {
          throw new Error('Page has no versions');
        }

        this.versions = this.page.fields.versions;

        if (!this.$route.params.version) {
          this.redirectToLatestPageVersion(slug);
          return;
        }

        this.setContent();
      } catch (error) {
        this.setIsPageLoading(false);
        console.error(error);
      }
    },

    redirectToLatestPageVersion(slug) {
      const latestVersion = this.versions[this.versions.length - 1].fields.versionNumber;

      this.$router.replace({
        name: 'version-api-reference',
        params: { slug, version: `v${latestVersion}` },
      });
    },

    setContent() {
      const { versions, page } = this;
      const versionNumber = this.$route.params.version.replace('v', '');
      const versionData = versions.find((v) => v.fields.versionNumber === versionNumber);

      if (!versionData) {
        this.redirectToLatestPageVersion(page.fields.slug);
        return;
      }

      const availableVersions = versions.map((v) => v.fields.versionNumber);

      this.content = {
        availableVersions,
        ...page.fields,
        ...versionData.fields,
      };
    },
  },
};
</script>

<template>
  <div>
    <template v-if="content">
      <ApiReferencePage :content="content" class="fade-in" :class="{ show: showContent }" />
    </template>

    <template v-else>
      <PageNotFound />
    </template>
  </div>
</template>

<style lang="scss" scoped>
.fade-in {
  opacity: 0;

  &.show {
    transition: 0.2s ease-in;
    opacity: 1;
  }
}
</style>
